import { useEffect, useState } from "react";
import { useParams, Link, useLocation } from "react-router-dom";
import { PageHeader } from "../../components/common/PageHeader";
import { StatusBadge } from "../../components/common/StatusBadge";
import { Button } from "@/components/ui/button";
import { PosService, AdminSalesService } from "../../services";
import { currency, dateTime } from "../../utils/format";
import { getSaleItemTypeLabel, isLiquorSaleItem } from "../../utils/barProducts";
import { ArrowLeft, Printer, Loader2, Info } from "lucide-react";

const BOTTLE_SALE_MODES = {
  FULL_BOTTLE: "FULL_BOTTLE",
  MEASURE_180ML: "MEASURE_180ML",
  MEASURE_90ML: "MEASURE_90ML",
};

const roundMoney = (value) => Math.round(Number(value || 0) * 100) / 100;

const getNumberValue = (...values) => {
  for (const value of values) {
    if (value !== null && value !== undefined && value !== "") {
      const numberValue = Number(value);

      if (Number.isFinite(numberValue)) {
        return numberValue;
      }
    }
  }

  return 0;
};

const getPaymentTotal = (sale, paymentMethod) =>
  (sale?.payments || [])
    .filter((payment) => payment.paymentMethod === paymentMethod)
    .reduce((sum, payment) => sum + getNumberValue(payment.amount), 0);

const getBottleTotal = (sale) =>
  getNumberValue(sale?.totalBottleAmount, sale?.bottleSalesAmount, sale?.bottleTotal);

const getRestaurantTotal = (sale) =>
  getNumberValue(
    sale?.totalBiteAmount,
    sale?.restaurantSalesAmount,
    sale?.biteSalesAmount,
    sale?.restaurantTotal
  );

const getSubtotal = (sale) =>
  getNumberValue(sale?.subtotalAmount, sale?.subTotal);

const isSaleSplitPayment = (sale) =>
  getPaymentTotal(sale, "CASH") > 0 && getPaymentTotal(sale, "CARD") > 0;

const calculateSalePaymentDivision = (sale) => {
  const bottleTotal = getBottleTotal(sale);
  const restaurantTotal = getRestaurantTotal(sale);
  const subtotal = getSubtotal(sale);
  const cashAmount = getPaymentTotal(sale, "CASH");
  const cardAmount = getPaymentTotal(sale, "CARD");

  const barRatio = subtotal > 0 ? bottleTotal / subtotal : 0;
  const restaurantRatio = subtotal > 0 ? restaurantTotal / subtotal : 0;

  return {
    bottleTotal: roundMoney(bottleTotal),
    restaurantTotal: roundMoney(restaurantTotal),
    subtotal: roundMoney(subtotal),
    cashAmount: roundMoney(cashAmount),
    cardAmount: roundMoney(cardAmount),
    barRatio,
    restaurantRatio,
    barCash: roundMoney(cashAmount * barRatio),
    restaurantCash: roundMoney(cashAmount * restaurantRatio),
    barCard: roundMoney(cardAmount * barRatio),
    restaurantCard: roundMoney(cardAmount * restaurantRatio),
  };
};

const formatRatioPercent = (ratio) => `${(Number(ratio || 0) * 100).toFixed(2)}%`;

export const SaleDetailPage = ({
  context = "admin",
  cashierBasePath = "/cashier",
  cashierMySalesPath,
} = {}) => {
  const { saleId } = useParams();
  const location = useLocation();

  const [sale, setSale] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    setLoading(true);
    setError(null);

    const request =
      context === "cashier"
        ? PosService.saleDetail(saleId)
        : AdminSalesService.detail(saleId);

    request
      .then(setSale)
      .catch((e) => setError(e?.response?.data?.message || "Sale not found"))
      .finally(() => setLoading(false));
  }, [saleId, context]);

  const backTo =
    context === "cashier"
      ? `${cashierMySalesPath || `${cashierBasePath}/sales/my`}${location.search || ""}`
      : "/admin/sales";

  if (loading) {
    return (
      <div className="grid place-items-center py-12 text-slate-500">
        <Loader2 className="h-5 w-5 animate-spin" />
      </div>
    );
  }

  if (error || !sale) {
    return (
      <div
        className="max-w-md mx-auto bms-card p-8 text-center"
        data-testid="sale-not-found"
      >
        <h2 className="font-display text-xl font-bold text-slate-900">
          Bill not found
        </h2>

        <p className="text-slsssate-500 mt-2 text-sm">
          {error || "This sale no longer exists."}
        </p>

        <Link to={backTo}>
          <Button className="mt-4 bg-navy-800 hover:bg-navy-900 text-white">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
        </Link>
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title={`Bill ${sale.saleCode || sale.billNumber}`}
        subtitle={`${dateTime(sale.createdAt || sale.saleDateTime)} · ${
          sale.cashierName || sale.cashierFullName || sale.cashierUsername
        }`}
        actions={
          <div className="flex gap-2">
            <Link to={backTo}>
              <Button variant="outline">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back
              </Button>
            </Link>

            <Button variant="outline" onClick={() => window.print()}>
              <Printer className="h-4 w-4 mr-2" />
              Print
            </Button>

            {context !== "cashier" && sale.status === "COMPLETED" && (
              <Link to={`/admin/sales/${sale.saleId}/manual-correction`}>
                <Button className="bg-amber-500 hover:bg-amber-600 text-navy-900">
                  Manual Correction
                </Button>
              </Link>
            )}

            {context === "cashier" && sale.status === "COMPLETED" && (
              <Link to={`${cashierBasePath}/sales/${sale.saleId}/correction`}>
                <Button className="bg-amber-500 hover:bg-amber-600 text-navy-900">
                  Request correction
                </Button>
              </Link>
            )}
          </div>
        }
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="bms-card p-5 lg:col-span-2">
          <h3 className="font-display text-lg font-semibold mb-3">Items</h3>

          <table className="w-full text-sm">
            <thead>
              <tr className="text-left border-b border-slate-200">
                <th className="py-2 text-xs font-semibold uppercase text-slate-500">
                  Item
                </th>

                <th className="py-2 text-xs font-semibold uppercase text-slate-500">
                  Type
                </th>

                <th className="py-2 text-xs font-semibold uppercase text-slate-500 text-right">
                  Qty
                </th>

                <th className="py-2 text-xs font-semibold uppercase text-slate-500 text-right">
                  Unit
                </th>

                <th className="py-2 text-xs font-semibold uppercase text-slate-500 text-right">
                  Total
                </th>
              </tr>
            </thead>

            <tbody>
              {sale.items.map((item) => {
                const isLiquor = isLiquorSaleItem(item);
                const isMeasuredSale =
                  isLiquor &&
                  item.bottleSaleMode &&
                  item.bottleSaleMode !== BOTTLE_SALE_MODES.FULL_BOTTLE;

                const totalMeasuredMl =
                  isMeasuredSale && item.measureMl != null
                    ? Number(item.measureMl) * Number(item.quantity || 0)
                    : null;

                return (
                  <tr
                    key={item.saleItemId}
                    className="border-b border-slate-100"
                  >
                    <td className="py-2">
                      <div className="font-medium text-slate-900">
                        {item.itemName}
                      </div>

                      {isLiquor && (
                        <div className="mt-0.5 text-xs">
                          {isMeasuredSale ? (
                            <>
                              <span className="font-medium text-amber-700">
                                {item.measureMl} ml serving
                              </span>

                              {totalMeasuredMl != null && (
                                <span className="text-slate-500">
                                  {" "}
                                  · Total {totalMeasuredMl} ml
                                </span>
                              )}

                              {item.measureUnits != null && (
                                <span className="text-slate-500">
                                  {" "}
                                  · {item.measureUnits} measure unit
                                  {Number(item.measureUnits) === 1 ? "" : "s"}
                                </span>
                              )}
                            </>
                          ) : (
                            <span className="text-slate-500">Full bottle</span>
                          )}
                        </div>
                      )}

                      {isLiquor &&
                        !isMeasuredSale &&
                        (Boolean(item.bottleReturned) || Number(item.bottleReturnUnitCredit || 0) > 0) && (
                          <div className="text-xs text-slate-500 mt-0.5">
                            Empty bottle:{" "}
                            {item.bottleReturned
                              ? "Returned"
                              : "Not returned"}
                          </div>
                        )}
                    </td>

                    <td className="py-2">
                    <StatusBadge value={getSaleItemTypeLabel(item, BOTTLE_SALE_MODES)} />
                    </td>

                    <td className="py-2 text-right">{item.quantity}</td>

                    <td className="py-2 text-right">
                      {currency(item.unitPrice)}
                    </td>

                    <td className="py-2 text-right font-medium">
                      {currency(item.totalPrice)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="space-y-4">
          <div className="bms-card p-5">
            <h3 className="font-display text-lg font-semibold mb-2">Totals</h3>

            <Row
              label="Sub total"
              value={currency(sale.subTotal ?? sale.subtotalAmount)}
            />

            <Row label="Discount" value={`- ${currency(sale.discountAmount)}`} />

            <Row label="Service" value={currency(sale.serviceChargeAmount)} />

            <Row label="Tax" value={currency(sale.taxAmount)} />

            <div className="mt-2 pt-2 border-t border-slate-200 flex justify-between">
              <span className="font-display font-semibold">Grand total</span>

              <span className="font-display font-bold text-lg">
                {currency(sale.grandTotal ?? sale.grandTotalAmount)}
              </span>
            </div>

            <div className="mt-3">
              <StatusBadge value={sale.status} />
            </div>
          </div>

          <div className="bms-card p-5">
            <h3 className="font-display text-lg font-semibold mb-2">
              Payments
            </h3>

            <ul className="space-y-2 text-sm">
              {sale.payments.map((payment) => (
                <li key={payment.paymentId} className="flex justify-between">
                  <span className="text-slate-700">
                    <StatusBadge value={payment.paymentMethod} />

                    {payment.referenceNumber
                      ? ` · ${payment.referenceNumber}`
                      : ""}
                  </span>

                  <span className="font-medium">
                    {currency(payment.amount)}
                  </span>
                </li>
              ))}
            </ul>
          </div>

          {isSaleSplitPayment(sale) && <PaymentDivisionCard sale={sale} />}

          {context !== "cashier" && sale.profit != null && (
            <div className="bms-card p-5">
              <h3 className="font-display text-lg font-semibold mb-2">
                Profit (bottles)
              </h3>

              <p className="text-2xl font-display font-bold text-emerald-600">
                {currency(sale.profit)}
              </p>

              <p className="text-xs text-slate-500 mt-1">
                FIFO batch profit captured at sale time.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};


const PaymentDivisionCard = ({ sale }) => {
  const division = calculateSalePaymentDivision(sale);

  return (
    <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50/70 p-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h4 className="font-display font-semibold text-slate-900">
            Payment Division
          </h4>
          <p className="text-xs text-amber-800 mt-1">
            Shown because this bill has both cash and card payments.
          </p>
        </div>
      </div>

      <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
        <div className="rounded-lg border border-white bg-white p-3 space-y-2">
          <div className="text-xs font-semibold uppercase tracking-wide text-emerald-700">
            Cash payment
          </div>
          <Row label="Bar cash" value={currency(division.barCash)} />
          <Row
            label="Restaurant cash"
            value={currency(division.restaurantCash)}
          />
        </div>

        <div className="rounded-lg border border-white bg-white p-3 space-y-2">
          <div className="text-xs font-semibold uppercase tracking-wide text-sky-700">
            Card payment
          </div>
          <Row label="Bar card" value={currency(division.barCard)} />
          <Row
            label="Restaurant card"
            value={currency(division.restaurantCard)}
          />
        </div>
      </div>

      <details className="mt-3 rounded-lg border border-amber-200 bg-white px-3 py-2 text-xs text-slate-700">
        <summary className="cursor-pointer list-none font-semibold text-navy-800 inline-flex items-center gap-1.5">
          <Info className="h-3.5 w-3.5" />
          Show exact calculation
        </summary>

        <div className="mt-3 space-y-1.5">
          <Row label="Bottle / Bar total" value={currency(division.bottleTotal)} />
          <Row
            label="Restaurant total"
            value={currency(division.restaurantTotal)}
          />
          <Row label="Subtotal" value={currency(division.subtotal)} />
          <Row
            label="Bar ratio"
            value={`${division.bottleTotal.toLocaleString()} / ${division.subtotal.toLocaleString()} = ${formatRatioPercent(division.barRatio)}`}
          />
          <Row
            label="Restaurant ratio"
            value={`${division.restaurantTotal.toLocaleString()} / ${division.subtotal.toLocaleString()} = ${formatRatioPercent(division.restaurantRatio)}`}
          />
          <Row label="Cash received" value={currency(division.cashAmount)} />
          <Row
            label="Bar cash"
            value={`${currency(division.cashAmount)} × ${formatRatioPercent(division.barRatio)} = ${currency(division.barCash)}`}
          />
          <Row
            label="Restaurant cash"
            value={`${currency(division.cashAmount)} × ${formatRatioPercent(division.restaurantRatio)} = ${currency(division.restaurantCash)}`}
          />
          <Row label="Card amount" value={currency(division.cardAmount)} />
          <Row
            label="Bar card"
            value={`${currency(division.cardAmount)} × ${formatRatioPercent(division.barRatio)} = ${currency(division.barCard)}`}
          />
          <Row
            label="Restaurant card"
            value={`${currency(division.cardAmount)} × ${formatRatioPercent(division.restaurantRatio)} = ${currency(division.restaurantCard)}`}
          />
        </div>
      </details>
    </div>
  );
};

const Row = ({ label, value }) => (
  <div className="flex justify-between py-1 text-sm text-slate-700">
    <span>{label}</span>
    <span>{value}</span>
  </div>
);
